import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { HelpersService } from '../../services/helpers.service';

export type JumpToItem = {
  buttonLabel: string;
  buttonUrl: string;
  buttonFragment: string;
  borders?: boolean;
};

@Component({
  selector: '[jump-to]',
  imports: [CommonModule, RouterModule],
  templateUrl: './jump-to.component.html',
  styleUrl: './jump-to.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'position-sticky top-0 bg-white d-block z-1',
    '(window:scroll)': 'checkSections()',
    '(window:resize)': 'checkSections()',
  },
})
export class JumpToComponent implements AfterViewInit, OnChanges {
  helpersService = inject(HelpersService);

  jumpToElHeight: number = 0;
  @Input() borders: boolean = true;
  @Input({ required: true })
  jumpToData: JumpToItem[] = [];
  @Input()
  activeSection: string | null = null;

  @Input() mobileWrap: boolean = false;
  @Input() reorderMobile: boolean = true;
  @Input() wrapIndex: number | null = null;

  @ViewChild('jumpTo') jumpTo!: ElementRef;

  constructor(private route: ActivatedRoute, private renderer: Renderer2) {}

  ngAfterViewInit(): void {
    this.jumpToElHeight = this.jumpTo.nativeElement.offsetHeight;
  }

  ngOnChanges(changes: SimpleChanges): void {}
  private isMobile(): boolean {
    if (!this.helpersService.validatePlatform()) return false;
    return this.helpersService.nativeWindow.innerWidth < 992; // < lg
  }

  get jumpToRenderData(): JumpToItem[] {
    // ✅ reorder SOLO cuando es mobile + flag activo
    if (!this.mobileWrap || !this.isMobile()) return this.jumpToData;

    if (this.reorderMobile && this.jumpToData.length >= 4) {
      const copy = [...this.jumpToData];
      [copy[2], copy[3]] = [copy[3], copy[2]]; // 3ro <-> 4to
      return copy;
    }

    return this.jumpToData;
  }
  scrollTo(id: string) {
    const targetElement = this.renderer.selectRootElement(`#${id}`, true);
    if (targetElement) {
      const elementPosition =
        targetElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - this.jumpToElHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }

    if (!this.helpersService.validatePlatform()) return;
    if (id === 'metabolic') {
      setTimeout(() => {
        const accordionToggle = this.helpersService.doc.getElementById(
          'metabolic-toggle'
        ) as HTMLElement;
        if (accordionToggle) {
          const isOpen =
            accordionToggle.getAttribute('aria-expanded') === 'true';
          if (!isOpen) accordionToggle.click();
        }
      }, 50);
    }
    if (id === 'beta-cell') {
      setTimeout(() => {
        const accordionToggle = this.helpersService.doc.getElementById(
          'beta-cell-toggle'
        ) as HTMLElement;
        if (accordionToggle) {
          const isOpen =
            accordionToggle.getAttribute('aria-expanded') === 'true';
          if (!isOpen) accordionToggle.click();
        }
      }, 50);
    }
  }

  checkSections() {
    //validate platform
    if (!this.helpersService.validatePlatform()) return;
    this.jumpToElHeight = this.jumpTo.nativeElement.offsetHeight;

    const sections = this.jumpToData
      .map((link) => {
        const element = this.helpersService.doc.querySelector(
          `#${link.buttonFragment}`
        ) as HTMLElement;
        return {
          link,
          element,
          top: element ? element.getBoundingClientRect().top : 0,
        };
      })
      .filter((item) => item.element !== null);

    // Sort by physical top position on the page (ascending order)
    sections.sort((a, b) => a.top - b.top);

    //this will highlight the section if visible
    sections.forEach((item) => {
      if (this.isElementInViewport(item.element)) {
        this.activeSection = item.link.buttonFragment;
      }
    });
  }

  isElementInViewport(el: any): boolean {
    //validate platform
    if (!this.helpersService.validatePlatform()) return false;

    //get element props
    const rect = el.getBoundingClientRect();

    //if element's top is in the middle of screen and bottom
    //has not left the screen, return true
    return (
      rect.top <=
        ((this.helpersService.nativeWindow.innerHeight ||
          this.helpersService.doc.documentElement.clientHeight) +
          this.jumpToElHeight) /
          3 && rect.bottom >= this.jumpToElHeight
    );
  }
}
